import ApiInstance from "./ApiInstance";
import { downloadCsv } from "../util/downloadCsv";
import { BarometerWelzijnAgg, BarometerBurnoutAgg, BarometerWerkAgg } from "./BarometerApi";

export interface OrgInfo {
  id: string;
  name: string;
  inviteCode: string;
  seatLimit?: number;
  memberCount: number;
  // k-safe: org-level settings + pool TOTALS only.
  fairUseCapPerMember: number;
  overagePoolRemaining: number;
  overagePoolGrantedTotal: number;
}
export interface OrgMembershipInfo {
  orgName: string;
  allowance: number;
  used: number;
  remaining: number;
  // usage_based: the org's anonymous overage pool can still fund the next
  // booking once the member's own cap is reached (boolean only).
  poolAvailable?: boolean;
}
export interface OrgDashboard {
  memberCount: number;
  kThreshold: number;
  suppressed: boolean;
  activeMembers: number | null;
  totalSessions: number | null;
  sessionsByType: Record<string, number> | null;
  outcome?: { suppressed: boolean; n: number | null; progressMean: number | null; usefulnessMean: number | null };
}
export interface OrgBillingUsage {
  year: number;
  month: number;
  countsByType: Record<string, number>;
  totalSessions: number;
  employerFeeCents: number;
  passThroughCents: number;
  estimatedInvoiceCents: number;
  annualFee: { headcount: number; tierLabel: string; feeCentsExclVat: number };
  fairUseCapPerMember: number;
  costAlertMonthlyCents: number | null;
  pool: { remaining: number; grantedTotal: number };
}

export interface OrgQuarterReportMonth {
  year: number;
  month: number;
  countsByType: Record<string, number>;
  totalSessions: number;
  employerFeeCents: number;
  passThroughCents: number;
}
export interface OrgQuarterReportWave {
  id: string;
  title: string;
  openedAt: string;
  status: string;
  suppressed: boolean;
  aggregate: { welzijn: BarometerWelzijnAgg | null; burnout: BarometerBurnoutAgg | null; werkbeleving: BarometerWerkAgg | null } | null;
}
// Automatische kwartaalreview: factuurgranulariteit + k-veilige adoptie +
// barometer-golven + één expliciet gelabelde modelled estimate (aannames mee).
export interface OrgQuarterReportBenchmark {
  minOrgs: number;
  orgs: number;
  adoptionPct: number | null;
  werkbeleving: number | null;
}

export interface OrgQuarterReport {
  benchmark: OrgQuarterReportBenchmark;
  outcome: { kThreshold: number; suppressed: boolean; n: number | null; progressMean: number | null; usefulnessMean: number | null };
  year: number;
  quarter: number;
  months: OrgQuarterReportMonth[];
  totals: {
    countsByType: Record<string, number>;
    totalSessions: number;
    employerFeeCents: number;
    passThroughCents: number;
    annualFeeQuarterCents: number;
    totalCostExclVatCents: number;
  };
  annualFee: { headcount: number; tierLabel: string; feeCentsExclVat: number };
  adoption: { memberCount: number; kThreshold: number; suppressed: boolean; activeMembers: number | null };
  barometer: { kThreshold: number; waves: OrgQuarterReportWave[] };
  modelledEstimate: {
    label: "modelled_estimate";
    assumptions: { absenceDayCostCents: number; avoidedAbsenceDaysPerSession: number };
    sessionsInPeriod: number;
    modelledAvoidedAbsenceDays: number;
    modelledValueCents: number;
    totalCostExclVatCents: number;
  };
  // Kosten-vs-waarde: zorgwaarde is echt (snapshot-tarieven), de €1→€X-ratio
  // is modelled; per-gebruiker-cijfers zijn k-onderdrukt (null onder k).
  financialImpact: {
    headcount: number;
    costPerEmployeeCents: number;
    careValueCents: number;
    effectiveCostPerUserCents: number | null;
    careValuePerUserCents: number | null;
    adoptionPctOfHeadcount: number | null; // % van het volledige personeelsbestand (k-safe)
    modelledTotalValueCents: number;
    modelledValuePerEuro: number;
  };
}

class OrgApi {
  async membership(): Promise<OrgMembershipInfo | null> {
    const { data } = await ApiInstance.getApi().get("/org/me");
    return data.membership;
  }
  async join(code: string): Promise<{ orgName: string }> {
    const { data } = await ApiInstance.getApi().post("/org/join", { code });
    return data;
  }
  async myOrg(): Promise<OrgInfo> {
    const { data } = await ApiInstance.getApi().get("/org");
    return data;
  }
  async dashboard(): Promise<OrgDashboard> {
    const { data } = await ApiInstance.getApi().get("/org/dashboard");
    return data;
  }
  async billingUsage(): Promise<OrgBillingUsage> {
    const { data } = await ApiInstance.getApi().get("/org/billing/usage");
    return data;
  }
  // k-safe billing levers: the collective fair-use cap and/or the aggregate
  // monthly cost-alert threshold (null clears the alert).
  async updateBillingSettings(body: { fairUseCapPerMember?: number; costAlertMonthlyCents?: number | null }): Promise<{ ok: boolean; fairUseCapPerMember: number; costAlertMonthlyCents: number | null }> {
    const { data } = await ApiInstance.getApi().patch("/org/billing/settings", body);
    return data;
  }
  // Grant pooled overage sessions (assigned anonymously, FIFO, by Carefunded).
  async grantPool(add: number): Promise<{ ok: boolean; overagePoolRemaining: number; overagePoolGrantedTotal: number }> {
    const { data } = await ApiInstance.getApi().post("/org/billing/pool", { add });
    return data;
  }
  // Year overview CSV — invoice granularity only (counts × type × month).
  async downloadUsageYearCsv(year: number): Promise<void> {
    await downloadCsv(`/org/billing/usage/export?year=${year}`, `verbruik-${year}.csv`);
  }
  async quarterReport(year: number, quarter: number): Promise<OrgQuarterReport> {
    const { data } = await ApiInstance.getApi().get(`/org/report?year=${year}&quarter=${quarter}`);
    return data;
  }
}

const orgApi = new OrgApi();
export default orgApi;
